import type { RefreshBlock } from './refreshLatch';

/**
 * Why the sign-in page is shown, when it has something to say about it: the
 * banner at the top of its card. `rateLimited` holds the button until
 * `until` (epoch ms). No session at all (first visit, `noSession`) says
 * nothing.
 */
export type SignInBanner =
  | { kind: 'expired' }
  | { kind: 'signedOut' }
  | { kind: 'rateLimited'; until: number };

/** The banner of a latched refresh block, if it has one. */
export function bannerFor(
  block: RefreshBlock | undefined,
): SignInBanner | undefined {
  switch (block?.reason) {
    case 'rateLimited':
      return { kind: 'rateLimited', until: block.until };
    case 'expired':
      return { kind: 'expired' };
    case 'signedOut':
      return { kind: 'signedOut' };
    default:
      return undefined;
  }
}

const pad = (value: number) => String(value).padStart(2, '0');

/** A wait in words: « 4 min 05 s », « 42 s ». */
export function formatWait(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return minutes > 0 ? `${minutes} min ${pad(rest)} s` : `${rest} s`;
}

/** A wait as a clock, for the button: « 4:05 », « 0:42 ». */
export function formatClock(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${pad(seconds % 60)}`;
}

function decode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    // A malformed escape (`%E9`): shown as it is in the address bar.
    return segment;
  }
}

/**
 * The page the user will come back to once signed in, in words, or
 * `undefined` for the home page. An environment (`/projets/<p>/<b>`, the
 * branch page) reads « <p> › <b> »; any other page, its path.
 */
export function returnPathLabel(pathname: string): string | undefined {
  const path = pathname.replace(/\/+$/, '');
  if (path === '') {
    return undefined;
  }
  const branchPage = /^\/projets\/([^/]+)\/([^/]+)/.exec(path);
  if (branchPage) {
    return `${decode(branchPage[1])} › ${decode(branchPage[2])}`;
  }
  return decode(path);
}

/** The most of a raw error message « Détails techniques » shows. */
export const MAX_ERROR_DETAIL = 300;

/** A raw error message cut to {@link MAX_ERROR_DETAIL} characters, « … » ending a cut one. */
export function errorDetail(message: string): string {
  const chars = Array.from(message);
  return chars.length > MAX_ERROR_DETAIL
    ? `${chars.slice(0, MAX_ERROR_DETAIL - 1).join('')}…`
    : message;
}
